/**
 * KAN-725: one-line summary of a campaign list upload.
 *
 * "Загружено {created}. Пропущено: в других списках {..}, уже клиенты {..},
 * повторы в файле {..}. В файле осталось новых: {remaining}"
 */
import type { UploadResult } from '@/types/dataset';

export function formatUploadSummary(result: UploadResult): string {
  const inLists = result.skipped_duplicates_in_lists ?? 0;
  const clients = result.skipped_existing_clients ?? 0;
  const inFile = result.skipped_in_file ?? 0;
  const remaining = result.remaining_new_in_file ?? 0;
  return (
    `Загружено ${result.created ?? 0}. ` +
    `Пропущено: в других списках ${inLists}, уже клиенты ${clients}, ` +
    `повторы в файле ${inFile}. В файле осталось новых: ${remaining}`
  );
}

/**
 * Parse the "Сколько загрузить" input: empty means "all" (undefined); anything
 * that is not a whole number >= 1 is invalid (null).
 */
export function parseUploadLimit(raw: string): number | undefined | null {
  const text = raw.trim();
  if (!text) return undefined;
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value >= 1 ? value : null;
}

/** Google Sheets link check mirrored from the backend (docs.google.com only). */
export function isGoogleSheetUrl(raw: string): boolean {
  try {
    const url = new URL(raw.trim());
    return (
      url.protocol === 'https:' &&
      url.hostname.toLowerCase() === 'docs.google.com' &&
      /^\/spreadsheets\/d\/[A-Za-z0-9_-]{10,}/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

/** KAN-826: counters of supplementing records and organizations, in display order. */
export const UPDATE_COUNTERS: ReadonlyArray<{
  key: 'updated_records' | 'updated_clients' | 'companies_filled' | 'companies_merged' | 'not_found';
  label: string;
}> = [
  { key: 'updated_records', label: 'Обновлено записей' },
  { key: 'updated_clients', label: 'Обновлено клиентов' },
  { key: 'companies_filled', label: 'Организаций заполнено' },
  { key: 'companies_merged', label: 'Организаций объединено' },
  { key: 'not_found', label: 'Не найдено' },
];

/** KAN-826: whether the result carries any non-zero update counter. */
export function hasUpdateCounters(result: UploadResult): boolean {
  return UPDATE_COUNTERS.some(({ key }) => (result[key] ?? 0) > 0);
}

/**
 * KAN-826: "Обновлено записей N, клиентов N, организаций заполнено N,
 * объединено N, не найдено N"; '' when every counter is zero or absent.
 */
export function formatUpdateSummary(result: UploadResult): string {
  if (!hasUpdateCounters(result)) return '';
  return (
    `Обновлено записей ${result.updated_records ?? 0}, ` +
    `клиентов ${result.updated_clients ?? 0}, ` +
    `организаций заполнено ${result.companies_filled ?? 0}, ` +
    `объединено ${result.companies_merged ?? 0}, ` +
    `не найдено ${result.not_found ?? 0}`
  );
}
